"use client";

import { useMemo, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { Heading } from "@/components/ui/typography";

export default function ForgotPasswordPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);
  const t = useTranslations("auth");
  const locale = useLocale();
  const searchParams = useSearchParams();
  const [signinHref, setSigninHref] = useState<string>(
    `/${locale}/auth/signin`,
  );

  const isDev = process.env.NODE_ENV !== "production";
  const h1w = searchParams?.get("h1w");
  const h1WeightClass = useMemo(() => {
    if (!isDev) return undefined;
    switch ((h1w || "").toLowerCase()) {
      case "bold":
        return "font-bold";
      case "semibold":
        return "font-semibold";
      case "medium":
        return "font-medium";
      default:
        return undefined;
    }
  }, [h1w, isDev]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("tm_auth_callbackUrl");
      const storedReason = localStorage.getItem("tm_auth_reason");
      const storedTs = Number(
        localStorage.getItem("tm_auth_callbackUrl_ts") || "0",
      );
      const isFresh =
        storedTs > 0 && Date.now() - storedTs < 24 * 60 * 60 * 1000;

      const qs = new URLSearchParams();
      if (
        isFresh &&
        stored &&
        stored.startsWith("/") &&
        !stored.startsWith("//")
      ) {
        qs.set("callbackUrl", stored);
      }
      if (storedReason) qs.set("reason", storedReason);

      const s = qs.toString();
      setSigninHref(`/${locale}/auth/signin${s ? `?${s}` : ""}`);
    } catch {
      setSigninHref(`/${locale}/auth/signin`);
    }
  }, [locale]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mounted) return;

    setIsLoading(true);
    setError(null);

    try {
      // 发送密码重置请求
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "发送重置密码邮件失败");
      }

      // 显示成功消息
      setSuccess(true);
    } catch (error) {
      console.error("忘记密码请求失败:", error);
      setError(
        error instanceof Error
          ? error.message
          : "发送重置密码邮件失败，请稍后再试",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // 如果组件未挂载，显示加载状态
  if (!mounted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white dark:bg-gray-800 p-6 shadow-md">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mx-auto mb-6"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { href: "/auth", name: t("breadcrumb.auth") },
            {
              href: "/auth/forgot-password",
              name: t("breadcrumb.forgot_password"),
            },
          ]}
        />
      </div>

      <div className="flex min-h-[calc(100vh-200px)] flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-rose-50 to-amber-50 dark:from-slate-900 dark:via-indigo-950/60 dark:to-amber-900/40 transition-colors">
        <div className="w-full max-w-md space-y-6 rounded-3xl border border-border/60 bg-card/95 p-8 shadow-xl backdrop-blur-sm dark:border-border/40">
          <Heading
            variant="h1"
            as="h1"
            className={`mb-2 text-center text-foreground font-semibold ${h1WeightClass ?? ""}`}
          >
            {t("forgotPassword.title")}
          </Heading>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success ? (
            <div className="space-y-4">
              <Alert className="mb-4">
                <AlertDescription>
                  {t("forgotPassword.success")}
                </AlertDescription>
              </Alert>
              <div className="text-center">
                <Link
                  href={signinHref}
                  className="text-sm font-medium text-primary hover:text-primary/80"
                >
                  {t("forgotPassword.backToLogin")}
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("forgotPassword.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("login.emailPlaceholder")}
                  required
                  data-testid="email-input"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                data-testid="forgot-password-button"
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-lg text-sm font-medium text-foreground bg-gradient-to-r from-primary/70 via-rose-300/70 to-amber-200/70 hover:from-primary/80 hover:via-rose-300/80 hover:to-amber-200/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:from-primary/60 dark:via-rose-400/60 dark:to-amber-200/60"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("register.sending")}
                  </>
                ) : (
                  t("forgotPassword.submit")
                )}
              </button>

              <div className="mt-4 text-center">
                <Link
                  href={signinHref}
                  className="text-sm font-medium text-primary hover:text-primary/80"
                >
                  {t("forgotPassword.backToLogin")}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
